import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './PollPrompt.scss';
import { ChatContext, events } from './ChatContext';

const cx = classNames.bind(styles);

interface RoomPoll {
  roomId: string;
  votes: Record<string, number>;
  options: string[];
  title: string;
  createdAt: number;
  createdBy: string;
  closedAt: number;
  duration?: number; // in seconds, optional
}

interface PollPromptProps {
  poll: RoomPoll;
  currentUser?: ChatUser;
}

function PollPrompt({ poll, currentUser }: PollPromptProps) {
  const [collapsed, setCollapsed] = useState(false);
  // Add state for time remaining
  const [timeRemaining, setTimeRemaining] = useState(
    poll.duration && !poll.closedAt
      ? Math.ceil((poll.createdAt + ((poll.duration ?? 0) * 1000) - Date.now()) / 1000)
      : 0
  );
  const totalVotes = useMemo(
    () => poll.options.reduce((sum, opt) => sum + (poll.votes[opt] || 0), 0),
    [poll]
  );
  const chat = useContext(ChatContext);

  const onVote = useCallback((option: string) => {
    chat.client?.emit(events.VOTE_POLL, {
      roomId: poll.roomId,
      option,
    });
  }, []);

  const tied = useMemo(() => {
    if (!poll.closedAt) {
      return false;
    }

    const firstVotes = poll.votes[poll.options[0]] || 0;

    return poll.options.every((option) => {
      const votes = poll.votes[option] || 0;
      return votes === firstVotes;
    });
  }, [poll]);

  const winnerOptions = useMemo(() => {
    if (!poll.closedAt || tied) {
      return null;
    }
    const maxVotes = Math.max(...poll.options.map((option) => poll.votes[option] || 0));

    if (maxVotes === 0) {
      return null;
    }

    return poll.options.filter((option) => (poll.votes[option] || 0) === maxVotes);
  }, [poll, tied]);

  const pollPermission = useMemo(() => currentUser?.username == poll.createdBy || currentUser?.role === 'streamer', [currentUser, poll.createdBy]);

  // Interval to update time remaining
  useEffect(() => {
    if (!poll.duration || poll.closedAt) return;
    const update = () => {
      setTimeRemaining(
        poll.duration
          ? Math.max(0, Math.ceil((poll.createdAt + (poll.duration * 1000) - Date.now()) / 1000))
          : 0
      );
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [poll.duration, poll.closedAt, poll.createdAt]);

  return (
    <div className={cx('PollPrompt')}>
      <div className={cx('PollPrompt__Header')}>
        <div className={cx('PollPrompt__Title')}>
          <span role="img" className="ion-stats-bars" />
          &nbsp;
          {poll.title}
        </div>
        {
          currentUser && (
            <button className={cx('PollPrompt__Toggle')} onClick={() => setCollapsed(!collapsed)} type="button">
              <span role="img" className={collapsed ? 'ion-chevron-up' : 'ion-chevron-down'} />
            </button>
          )
        }

      </div>
      {/** Display a time remaining bar if a poll duration is set */}
      {
        poll.duration && !poll.closedAt && (
          <div className={cx('PollPrompt__Duration')} title={`Time remaining: ${timeRemaining} seconds`}>
            <div
              className={cx('PollPrompt__DurationBar')}
              style={{
                width: `${Math.max(0, Math.min(100, (1 - ((Date.now() - poll.createdAt) / ((poll.duration ?? 1) * 1000))) * 100))}%`,
              }}
            />
          </div>
        )
      }

      <div className={cx('PollPrompt__Collapsible', { 'PollPrompt__Collapsed': collapsed })}>
        <div className={cx('PollPrompt__CreatedBy')}>
          Started by <b>{poll.createdBy}</b>
        </div>
        <PollResults 
          poll={poll}
          totalVotes={totalVotes}
          winnerOptions={winnerOptions}
          tied={tied}
          onVote={(option) => {
            if (!poll.closedAt) {
              onVote(option);
            }
          }}
        />
        <div className={cx('PollPrompt__Footer')}>
          <span className={cx('PollPrompt__TotalVotes')}>Total votes: {totalVotes}</span>
          {
            ((pollPermission) && !poll.closedAt) && (
              <button
                className={cx('PollPrompt__CloseButton')}
                onClick={() => chat.client?.emit(events.END_POLL, { roomId: poll.roomId })}
                type="button"
              >
                Close Poll
              </button>
            )
          }

          {
            poll.closedAt && (pollPermission) && (
              <button 
                className={cx('PollPrompt__CloseButton')}
                onClick={() => chat.client?.emit(events.CLEAR_POLL, { roomId: poll.roomId })}
                type="button"
              >
                <span role="img" className="ion-trash-a" />
              </button>
            )
          }
        </div>
      </div>
    </div>
  );
}

export function ChatPollResults({ poll }: { poll: RoomPoll }) {
  const totalVotes = useMemo(() => poll.options.reduce((sum, opt) => sum + (poll.votes[opt] || 0), 0), [poll]);
  const tied = useMemo(() => {
    if (!poll.closedAt) {
      return false;
    } 

    const firstVotes = poll.votes[poll.options[0]] || 0;

    return poll.options.every((option) => {
      const votes = poll.votes[option] || 0;
      return votes === firstVotes;
    });

  }, [poll]);

  const winnerOptions = useMemo(() => {
    if (!poll.closedAt || tied) {
      return null;
    }
    const maxVotes = Math.max(...poll.options.map((option) => poll.votes[option] || 0));

    if (maxVotes === 0) {
      return null;
    }

    return poll.options.filter((option) => (poll.votes[option] || 0) === maxVotes);
  }, [poll, tied]);


  return (
    <div>
      <PollResults
        poll={poll}
        totalVotes={totalVotes}
        winnerOptions={winnerOptions}
        tied={tied}
      />
    </div>
  );
}

function PollResults({ poll, totalVotes, winnerOptions, tied, onVote }: { poll: RoomPoll, totalVotes: number, winnerOptions: string[] | null, tied: boolean, onVote?: (option: string) => void }) {
  return (
    <div className={cx('PollPrompt__Options', { 'PollPrompt__Options--Closed': poll.closedAt })}>
      {poll.options.map((option) => {
        const votes = poll.votes[option] || 0;
        const percent = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
        return (
          <button
            key={option}
            style={{} as React.CSSProperties}
            className={cx('PollPrompt__Option', { 'PollPrompt__Option--Winner': winnerOptions?.includes(option) })}
            disabled={!!poll.closedAt}
            onClick={() => onVote && !poll.closedAt ? onVote(option) : undefined}
          >
            <div style={{ width: `${percent}%` }} className={cx('PollPrompt__OptionProgress')} />
            <div className={cx('PollPrompt__OptionContent')}>
              {tied && poll.closedAt && (
                <span className={cx('PollPrompt__Tied')}>
                  <span role="img" className="ion-minus-circled" />
                  &nbsp;
                </span>
              )}
              {winnerOptions?.includes(option) && poll.closedAt ? (
                <span>
                  <span role="img" className="ion-checkmark-circled" />
                  &nbsp;
                </span>
              ) : ''}
              <span className={cx('PollPrompt__OptionText')}>{option}</span>
              <span className={cx('PollPrompt__Votes')}>{votes} vote{votes !== 1 ? 's' : ''} ({percent}%)</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default PollPrompt;